// The shell's top bar (UAT plan D-2; SH-2, SH-3, SH-4, SH-5, SH-15): D0's TopBar over the content column, where the
// frame places it (column 2, row 1; shell/MissionShell.tsx). It holds the breadcrumb (shell/crumbs.tsx), Search, ? and
// the bell, and the Assistant button beside the bell when the frame hands it one (UAT F-146); the phone's Menu (or the
// desktop's navigation chevron) leads it and the recording-on-behalf state sits in its extra slot.
//   Search opens the glass D0 Dialog, from the button or on ⌘K (Ctrl+K); `shell.search` answers trimmed on the server
//     (requisitions in the seat's list scope, candidates only for a seat that searches them) and every hit links to its
//     screen. Escape closes it and focus returns where it was.
//   The bell carries the red dot while `shell.nav` counts unread notices (Q-D1: a dot, read-only notices only) and opens
//     the notices popover, which reads `shell.notices` (that open marks what it shows read) and clears the dot. The bell
//     is handed the popover's id only while the popover is drawn (UAT F-100).
//   ? is headed by the server's heading (*Resource Center*), lists the guidance pages the person may open and ends,
//     after a hairline, with Report a bug (`top.bug`), whose flow is the D0 Dialog in bug/BugButton.tsx. The shell holds
//     it open, so a navigation closes it (UAT F-69), as it closes the account menu in shell/MissionShell.tsx.

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { shellNotices, shellSearch, type NavItemOut, type NoticeOut, type SearchHit, type ShellNavOutput, type ShellSearchOutput } from "@/api/tools";
import { errorText, useRead } from "@/api/useTool";
import { BugReport } from "@/bug/BugButton";
import { Banner, Field, Input, Paragraph, Pill, Skeleton, type MenuItem } from "@/mc/core";
import { SHELL_LABELS, TopBar, type TopBarAssistant } from "@/mc/g4a";
import { Dialog } from "@/mc/g4b";
import { NAV_ITEM_LABELS } from "@/mc/labels";
import { formatDateTime } from "@/mc/lib";
import type { Route } from "@/router";
import { useSession } from "@/session/useSession";
import { shellCrumbs, useViewCrumbs } from "./crumbs";
import { SHELL_NAV_KEY } from "./nav";

/** The shortest query `shell.search` answers (its SEARCH_MIN). */
const SEARCH_MIN = 2;
const SEARCH_KEYS = "Meta+K Control+K";
const RESULTS_ID = "shell-search-results";
const NOTICES_ID = "shell-notices";

/**
 * A frame menu (?, the account menu) closes when the page moves on (UAT F-69): every hashchange, which the router
 * dispatches for each navigation it commits and the browser for Back and Forward, calls `close`. Returns the detach.
 */
export function closeOnHashchange(target: Pick<Window, "addEventListener" | "removeEventListener">, close: () => void): () => void {
  target.addEventListener("hashchange", close);
  return () => target.removeEventListener("hashchange", close);
}

/** ⌘K or Ctrl+K, with no other modifier: open Search from anywhere. */
export function isSearchShortcut(event: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k";
}

/** The ? menu: the guidance pages the server lists for the person, then Report a bug (Law 14: every role, every screen). */
export function helpItems(help: readonly NavItemOut[], onBug: () => void): MenuItem[] {
  return [
    ...help.map((item) => ({ label: item.label, href: item.route, hs: `top.help.${item.key}` })),
    { label: "Report a bug", onSelect: onBug, hs: "top.bug" },
  ];
}

export interface ShellTopBarProps {
  nav: ShellNavOutput | undefined;
  route: Route;
  /** Before everything else: the phone's Menu button, or the desktop's navigation chevron. */
  lead?: ReactNode | undefined;
  /** The Assistant button after the bell (UAT F-146): the frame holds the panel and its state. */
  assistant?: TopBarAssistant | undefined;
  /** The notices popover starts open (as D0's defaultOpen). */
  defaultNoticesOpen?: boolean | undefined;
}

export function ShellTopBar({ nav, route, lead, assistant, defaultNoticesOpen = false }: ShellTopBarProps) {
  const { me, can } = useSession();
  const [search, setSearch] = useState(false);
  const [notices, setNotices] = useState(defaultNoticesOpen);
  const [help, setHelp] = useState(false);
  const [bug, setBug] = useState(false);
  const bell = useRef<HTMLButtonElement | null>(null);
  const view = useViewCrumbs();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isSearchShortcut(event)) return;
      event.preventDefault();
      setNotices(false);
      setSearch(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // A navigation (a hit or a notice followed, the back button) closes Search and the popover; a bug report stays.
  useEffect(() => { setSearch(false); setNotices(false); }, [route.raw]);
  useEffect(() => (help ? closeOnHashchange(window, () => setHelp(false)) : undefined), [help]);
  const crumbs = shellCrumbs(route, nav?.items ?? [], view);

  return (
    <>
      <TopBar
        crumbs={crumbs}
        lead={lead}
        extra={me?.on_behalf_of ? <Pill tone="warn">{`Recording on behalf of ${me.on_behalf_of.name}`}</Pill> : null}
        onSearch={() => { setNotices(false); setSearch(true); }}
        searchKeys={SEARCH_KEYS}
        help={helpItems(nav?.help ?? [], () => setBug(true))}
        helpLabel={nav?.help_heading ?? SHELL_LABELS.processGuide}
        helpOpen={help}
        onHelpOpenChange={setHelp}
        unread={(nav?.notices_unread ?? 0) > 0}
        onBell={() => setNotices((open) => !open)}
        bellOpen={notices}
        bellRef={bell}
        bellControls={notices ? NOTICES_ID : undefined}
        assistant={assistant}
        hs={{ search: "top.search", help: "top.help", bell: "top.bell", assistant: "top.assistant" }}
      />
      <SearchDialog open={search} onClose={() => setSearch(false)} candidates={can("candidate.search")} />
      {notices ? <NoticesPopover anchor={bell} timeZone={me?.timezone} onClose={(refocus) => { setNotices(false); if (refocus) bell.current?.focus(); }} /> : null}
      <BugReport open={bug} onClose={() => setBug(false)} screen={crumbs.map((crumb) => crumb.label).join(" › ")} />
    </>
  );
}

// ---- Search -------------------------------------------------------------------------------------------------------

/** Search (SH-3): the glass D0 Dialog with the field first and the hits under it as the person types. */
export function SearchDialog({ open, onClose, candidates }: { open: boolean; onClose: () => void; candidates: boolean }) {
  const input = useRef<HTMLInputElement | null>(null);
  const [text, setText] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const id = window.setTimeout(() => setTerm(text.trim()), 200);
    return () => window.clearTimeout(id);
  }, [text]);
  useEffect(() => {
    if (!open) { setText(""); setTerm(""); }
  }, [open]);
  const ready = open && term.length >= SEARCH_MIN;
  const found = useRead<ShellSearchOutput>(["shell-search", term], () => shellSearch({ q: term }), { enabled: ready, staleTime: 30_000 });
  const hint = candidates
    ? "An R-id, a REQ ID or title words; a candidate's name or Candidate ID."
    : "An R-id, a REQ ID or title words.";
  return (
    <Dialog title={SHELL_LABELS.search} open={open} onClose={onClose} initialFocus={input} width={560}>
      <Field label={SHELL_LABELS.search} hint={hint}>
        <Input ref={input} type="search" icon="search" value={text} onChange={(event) => setText(event.target.value)} autoComplete="off" aria-controls={RESULTS_ID} />
      </Field>
      <div id={RESULTS_ID} className="mc-search-res" aria-live="polite" aria-busy={ready && found.isFetching ? true : undefined}>
        {!ready ? null
          : found.isPending ? <Skeleton lines={3} />
          : found.isError ? <Banner tone="red">{errorText(found.error)}</Banner>
          : <SearchHits result={found.data} candidates={candidates} onPick={onClose} />}
      </div>
    </Dialog>
  );
}

/** The hits by kind, each a link to its screen; a row the person's list shows but they cannot open has no link. */
export function SearchHits({ result, candidates, onPick }: { result: ShellSearchOutput; candidates: boolean; onPick: () => void }) {
  const sections = [
    { title: NAV_ITEM_LABELS.requisitions, hits: result.requisitions },
    ...(candidates ? [{ title: NAV_ITEM_LABELS.candidates, hits: result.candidates }] : []),
  ].filter((section) => section.hits.length > 0);
  if (sections.length === 0) return <Paragraph>Nothing matches.</Paragraph>;
  return (
    <>
      {sections.map((section) => (
        <section key={section.title}>
          <h3 className="mc-search-h">{section.title}</h3>
          <ul className="mc-search-list">
            {section.hits.map((hit) => <li key={hit.id}><Hit hit={hit} onPick={onPick} /></li>)}
          </ul>
        </section>
      ))}
    </>
  );
}

function Hit({ hit, onPick }: { hit: SearchHit; onPick: () => void }) {
  const words = (
    <>
      <span className="mc-mono">{hit.id}</span>
      <span className="mc-search-t1">{hit.title}</span>
      {hit.detail ? <span className="mc-mono mc-t3">{hit.detail}</span> : null}
    </>
  );
  return hit.route
    ? <a className="mc-search-hit" href={hit.route} onClick={onPick}>{words}</a>
    : <div className="mc-search-hit mc-search-off">{words}</div>;
}

// ---- the bell's notices ------------------------------------------------------------------------------------------

/**
 * The notices popover (SH-4, AC-25): a non-modal dialog under the bell. Each open reads `shell.notices` afresh (the
 * read marks what it shows read) and then refreshes `shell.nav`, so the dot goes. Escape returns to the bell; a press
 * outside closes it.
 */
function NoticesPopover({ anchor, timeZone, onClose }: {
  anchor: RefObject<HTMLButtonElement | null>;
  timeZone: string | undefined;
  onClose: (refocus: boolean) => void;
}) {
  const panel = useRef<HTMLDivElement | null>(null);
  const queries = useQueryClient();
  const [opened] = useState(() => Date.now());
  const read = useQuery({ queryKey: ["shell-notices", opened], queryFn: () => shellNotices({}), retry: false, staleTime: Infinity, gcTime: 0 });
  useEffect(() => {
    if (read.isSuccess) void queries.invalidateQueries({ queryKey: SHELL_NAV_KEY });
  }, [read.isSuccess, queries]);
  useEffect(() => { panel.current?.focus(); }, []);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      const node = event.target as Node | null;
      if (panel.current?.contains(node) || anchor.current?.contains(node)) return;
      close.current(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [anchor]);
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    close.current(true);
  };
  return (
    <div ref={panel} id={NOTICES_ID} className="mc-notices" role="dialog" aria-labelledby={`${NOTICES_ID}-h`} tabIndex={-1} onKeyDown={onKeyDown}>
      <h2 id={`${NOTICES_ID}-h`} className="mc-notices-h">Notifications</h2>
      {read.isPending ? <Skeleton lines={3} />
        : read.isError ? <Banner tone="red">{errorText(read.error)}</Banner>
        : <NoticeList items={read.data.items} timeZone={timeZone} onPick={() => close.current(false)} />}
    </div>
  );
}

/** The person's notices newest first (as the server sends them), the ones new to this open marked, each to its screen. */
export function NoticeList({ items, timeZone, onPick }: { items: readonly NoticeOut[]; timeZone: string | undefined; onPick: () => void }) {
  if (items.length === 0) return <Paragraph>No notifications.</Paragraph>;
  return (
    <ul className="mc-notices-list">
      {items.map((notice) => (
        <li key={notice.id} className={notice.read ? "mc-notice" : "mc-notice mc-notice-new"}>
          {notice.route
            ? <a className="mc-notice-t" href={notice.route} onClick={onPick}>{notice.title}</a>
            : <span className="mc-notice-t">{notice.title}</span>}
          {notice.body ? <p className="mc-notice-b">{notice.body}</p> : null}
          <span className="mc-notice-at">{formatDateTime(notice.created_at, timeZone)}</span>
        </li>
      ))}
    </ul>
  );
}
